import type { FC } from 'react';

import ErrorSection from 'components/ErrorSection/ErrorSection';
import Popup from 'components/Popup/Popup';
import SuccessSection from 'components/SuccessSection/SuccessSection';
import Typography from 'components/Typography/Typography';
import { useResponsive } from 'contexts/ResponsiveContext';
import { ScrollLockIdE } from 'declarations/enums/scroll';
import type { NoopT } from 'declarations/types/common';

import useTicketReopenPopup from './hooks/useTicketReopenPopup';
import {
  ActionBtnStyled,
  ControlsWrapStyled,
  FlexContainerMessageStyled,
  getPopupContentStyle,
} from './TicketReopenPopup.styled';

type TicketReopenPopupPropsT = {
  ticketId: string;
  isOpen: boolean;
  onClose: NoopT;
};

const TicketReopenPopup: FC<TicketReopenPopupPropsT> = ({ ticketId, isOpen, onClose }) => {
  const { isMobile } = useResponsive();
  const {
    isReopeningTicket,
    isTicketSuccessfullyReopened,
    isTicketReopeningError,
    showPopupContent,
    handleReopenTicket,
    handleClosePopup,
    reopenTicketErrorMessage,
    reopenTicketValidationErrorMessage,
  } = useTicketReopenPopup();

  return (
    <Popup
      nested
      isOpen={isOpen}
      showCloseBtn={isTicketSuccessfullyReopened || isTicketReopeningError}
      popupId={ScrollLockIdE.TicketReopenPopup}
      contentStyle={getPopupContentStyle(isMobile)}
      onClose={handleClosePopup(onClose)}
      closeOnEscape={false}
    >
      {showPopupContent && (
        <>
          <Typography color="primaryText" variant="h2" align={isMobile ? 'center' : 'left'}>
            Are you sure you want to reopen the ticket?
          </Typography>
          <ControlsWrapStyled justify={isMobile ? 'center' : 'flex-end'}>
            <ActionBtnStyled
              type="button"
              variant="outline"
              size="xl"
              onClick={handleClosePopup(onClose)}
            >
              Cancel
            </ActionBtnStyled>
            <ActionBtnStyled
              variant="contained"
              size="xl"
              isLoading={isReopeningTicket}
              onClick={handleReopenTicket([ticketId])}
            >
              Reopen
            </ActionBtnStyled>
          </ControlsWrapStyled>
        </>
      )}

      {isTicketSuccessfullyReopened && (
        <FlexContainerMessageStyled direction="column">
          <SuccessSection message="Ticket successfully reopened" />
        </FlexContainerMessageStyled>
      )}

      {isTicketReopeningError && (
        <FlexContainerMessageStyled direction="column">
          <ErrorSection
            errorDetails={reopenTicketErrorMessage || reopenTicketValidationErrorMessage}
          />
        </FlexContainerMessageStyled>
      )}
    </Popup>
  );
};

export default TicketReopenPopup;
